import {
  validateSubtestScore,
  validateCompositeScore,
  getScoreValidationError,
} from '../../../utils/scoreUtils';
import { subtestNames, compositeNames } from './constants';
import type { Interpretation } from '../types';
import { isThinkingCapableGeminiId } from '../../../api/catalog/geminiModels';

// Re-export the canonical WISC-V range validators for the old UI layer
export { validateSubtestScore, validateCompositeScore, getScoreValidationError };

/**
 * Collects human-readable validation errors for all entered scores.
 * Empty fields are ignored (a score does not have to be entered),
 * but every entered value must fall within the WISC-V ranges
 * (subtests 1-19, composite indices 40-160).
 */
export const collectScoreValidationErrors = (
  subtestScores: Record<string, string>,
  compositeScores: Record<string, string>
): string[] => {
  const errors: string[] = [];

  for (const [key, value] of Object.entries(subtestScores)) {
    if (value !== '' && value !== undefined && value !== null && !validateSubtestScore(value)) {
      errors.push(`${subtestNames[key] || key}: "${value}" — dozwolony zakres 1-19`);
    }
  }

  for (const [key, value] of Object.entries(compositeScores)) {
    if (value !== '' && value !== undefined && value !== null && !validateCompositeScore(value)) {
      errors.push(`${compositeNames[key] || key}: "${value}" — dozwolony zakres 40-160`);
    }
  }

  return errors;
};

// Version-based (Gemini pro/flash/flash-lite >= 2.5) — shared with the backend,
// so new releases (3.6/3.7/3.8-flash…) are covered without a hardcoded list.
export const supportsThinkingMode = (modelId: string): boolean => isThinkingCapableGeminiId(modelId);

export const getPercentile = (score: string | number, type: 'composite' | 'subtest' = 'composite'): number | null => {
  if (score === undefined || score === null || score === '') return null;
  const numScore = typeof score === 'string' ? parseInt(score) : score;
  if (isNaN(numScore) || numScore <= 0) return null;

  const mean = type === 'composite' ? 100 : 10;
  const sd = type === 'composite' ? 15 : 3;
  const z = (numScore - mean) / sd;

  // Error function approximation (erf)
  const t = 1.0 / (1.0 + 0.3275911 * Math.abs(z / Math.sqrt(2)));
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const erf = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(- (z / Math.sqrt(2)) * (z / Math.sqrt(2)));
  const cdf = z >= 0 ? 0.5 * (1.0 + erf) : 0.5 * (1.0 - erf);

  const pct = cdf * 100;
  if (pct < 0.1) return 0.1;
  if (pct > 99.9) return 99.9;
  return Math.round(pct * 10) / 10;
};

export interface Discrepancy {
  domainA: string;
  domainB: string;
  scoreA: number;
  scoreB: number;
  diff: number;
  isSignificant: boolean;
  message: string;
}

export const calculateDiscrepancies = (compositeScores: Record<string, string>): Discrepancy[] => {
  const domains = ['vci', 'vsi', 'fri', 'wmi', 'psi'];
  const domainLabels: Record<string, string> = {
    vci: 'Wskaźnik Rozumienia Słownego (VCI)',
    vsi: 'Wskaźnik Przestrzenny (VSI)',
    fri: 'Wskaźnik Rozumowania Płynnego (FRI)',
    wmi: 'Wskaźnik Pamięci Roboczej (WMI)',
    psi: 'Wskaźnik Szybkości Przetwarzania (PSI)'
  };

  const results: Discrepancy[] = [];

  for (let i = 0; i < domains.length; i++) {
    for (let j = i + 1; j < domains.length; j++) {
      const domA = domains[i];
      const domB = domains[j];
      const valAStr = compositeScores[domA];
      const valBStr = compositeScores[domB];

      if (valAStr && valBStr && valAStr !== '' && valBStr !== '') {
        const valA = parseInt(valAStr);
        const valB = parseInt(valBStr);
        if (!isNaN(valA) && !isNaN(valB)) {
          const diff = Math.abs(valA - valB);
          const isSignificant = diff >= 15;
          const higher = valA > valB ? domainLabels[domA] : domainLabels[domB];
          const labelA = domainLabels[domA];
          const labelB = domainLabels[domB];
          
          let message = '';
          if (isSignificant) {
            message = `Istotna statystycznie rozbieżność (${diff} pkt) pomiędzy ${labelA} a ${labelB} na korzyść ${higher}.`;
          } else {
            message = `Brak istotnej rozbieżności (${diff} pkt) pomiędzy ${labelA} a ${labelB}.`;
          }

          results.push({
            domainA: domA,
            domainB: domB,
            scoreA: valA,
            scoreB: valB,
            diff,
            isSignificant,
            message
          });
        }
      }
    }
  }

  return results;
};

export const getScoreInterpretation = (score: string | number, type: 'composite' | 'subtest' = 'composite'): string => {
  if (!score || score === '' || score === 0) return '';

  const numScore = typeof score === 'string' ? parseInt(score) : score;
  if (isNaN(numScore) || numScore <= 0) return '';

  if (type === 'composite') {
    if (numScore >= 130) return 'Bardzo wysoki';
    if (numScore >= 115) return 'Ponadprzeciętny';
    if (numScore >= 85) return 'Przeciętny';
    if (numScore >= 70) return 'Graniczny';
    return 'Bardzo niski';
  } else {
    if (numScore >= 16) return 'Bardzo wysoki';
    if (numScore >= 13) return 'Wysoki';
    if (numScore >= 8) return 'Przeciętny';
    if (numScore >= 5) return 'Niski';
    return 'Bardzo niski';
  }
};

// ---------------------------------------------------------------------------
// buildMarkdownFromSections — edycja diagnozy jako całości (PLAN-REFACTOR 4.2)
// ---------------------------------------------------------------------------
// Składa interpretację z powrotem do pełnego markdownu, gdy brak surowej
// odpowiedzi AI (savedRawText). Nagłówki są KANONICZNE względem synonimów
// responseParsera (src/services/responseParser.ts → extractMarkdownSections):
// muszą się kończyć dokładnie tam, gdzie kończy się dopasowanie regexa
// nagłówka — każdy sufiks (np. „## Ogólna ocena ROZWOJU") wyciekłby do treści
// sekcji przy re-parsowaniu. Listy zaleceń: „- pozycja" (parser je z powrotem
// dzieli na pozycje). Round-trip jest testowany w
// src/__tests__/buildMarkdownFromSections.test.ts.

/** Normalizuje treść listy zaleceń (string[] | string) do listy pozycji. */
const toRecommendationItems = (content: string[] | string | undefined): string[] => {
  if (!content) return [];
  const items = Array.isArray(content)
    ? content
    : content.split('\n').map((line) => line.replace(/^\s*[-•*]\s*/, ''));
  return items.map((item) => item.trim()).filter((item) => item !== '');
};

/**
 * Buduje pełny markdown diagnozy z sekcji interpretacji.
 * Puste sekcje są pomijane (round-trip: puste zostaje puste).
 */
export const buildMarkdownFromSections = (interpretation: Interpretation): string => {
  const parts: string[] = [];

  const pushText = (header: string, content: string | undefined) => {
    const trimmed = (content ?? '').trim();
    if (trimmed) parts.push(`## ${header}\n\n${trimmed}`);
  };
  const pushList = (header: string, content: string[] | string | undefined) => {
    const items = toRecommendationItems(content);
    if (items.length > 0) parts.push(`## ${header}\n\n${items.map((item) => `- ${item}`).join('\n')}`);
  };

  pushText('Ogólna ocena', interpretation.general_assessment);
  pushText('Mocne strony', interpretation.strengths);
  pushText('Obszary wymagające wsparcia', interpretation.areas_for_support || interpretation.difficulties);
  pushText('Funkcjonowanie szkolne', interpretation.classroom_implications || interpretation.school_functioning);
  pushList('Zalecenia dla nauczycieli', interpretation.teacher_recommendations);
  pushList('Zalecenia dla rodziców', interpretation.parent_recommendations);

  return parts.join('\n\n');
};

export const renderMarkdownToHtml = (md: string): string => {
  if (!md) return '';
  let html = md
    // Escape HTML entities first
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // ### Headers
    .replace(/^### (.+)$/gm, '<h3 style="font-size:15px;font-weight:600;color:#1e293b;margin:16px 0 8px;">$1</h3>')
    // ## Headers
    .replace(/^## (.+)$/gm, '<h2 style="font-size:18px;font-weight:700;color:#0f172a;margin:20px 0 10px;padding-bottom:4px;border-bottom:1px solid #e2e8f0;">$1</h2>')
    // **bold**
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#0f172a;font-weight:600;">$1</strong>')
    // *italic*
    .replace(/\*(.+?)\*/g, '<em style="color:#475569;">$1</em>')
    // List items (- or *)
    .replace(/^[\s]*[-•*]\s+(.+)$/gm, '<li style="margin-bottom:6px;color:#334155;">$1</li>')
    // Numbered list items
    .replace(/^[\s]*\d+\.\s+(.+)$/gm, '<li style="margin-bottom:6px;color:#334155;">$1</li>');

  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li[^>]*>.*?<\/li>\s*)+)/g, '<ul style="padding-left:20px;margin:8px 0;list-style-type:disc;">$1</ul>');

  // Paragraphs: double newlines
  html = html.split(/\n{2,}/).map(block => {
    const trimmed = block.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('<h') || trimmed.startsWith('<ul') || trimmed.startsWith('<li')) return trimmed;
    return `<p style="margin:8px 0;color:#334155;line-height:1.75;">${trimmed.replace(/\n/g, '<br/>')}</p>`;
  }).join('');

  return html;
};
